/* ==========================================================================
   RAWTORQUE · FIND PARTS - DESKTOP FITMENT FINDER · v2.0
   On desktop (>=1001px) the "Find Parts" button opens a fitment finder popover
   instead of the catalogue menu. We intercept the click in the CAPTURE phase on
   document, so the theme's own nav-burger handler (bubble phase) never runs -
   the catalogue menu never opens on desktop and the drawer's fixed close button
   never appears. Below 1001px we do nothing: the native mobile drawer + My
   Garage behave exactly as before.

   WHAT CHANGED IN v2.0 (2026-08-24)
   1. ROUTING: picks now land on the REAL collection wherever one exists
      (Classic 350 -> /collections/royal-enfield-classic-350-accessories-1,
      Audi -> /collections/audi-car-performance-parts-accessories). Only when
      no collection exists do we fall back to /search?q=<term> as before.
   2. Brand alone is now enough for bikes too - "Find parts" enables on brand
      and lands on the brand collection; the model step is optional.
   3. Brand list aligned with /pages/part-search: added Hero, Triumph, BMW,
      Kawasaki, Harley-Davidson, Benelli, Jawa & Yezdi (bikes) and the full
      car brand set.
   4. Footer of the popover now points at /pages/part-search (browse by
      vehicle) and /pages/part-search#help (fitment help).
   5. Exposes window.RT_FITMENT { BIKES, CARS, urlFor, POPULAR } and fires
      document event 'rt:fitment-ready' so the Part Search page can drive its
      own inline finder off the SAME data - one source of truth.

   MAINTENANCE: to add a vehicle, edit BIKES / CARS below. Value is the
   collection handle, or null to fall back to search.
   ========================================================================== */
(function () {
  'use strict';
  if (window.__rtFindParts) return;
  window.__rtFindParts = true;

  var MOBILE_MAX = 1000; // anything <= this keeps the native drawer

  /* -- DATA ------------------------------------------------------------
     BIKES: brand -> { c: brand collection handle, m: [[model, handle|null]] }
     A null handle means "no collection for this yet" -> /search?q=<model>.
     A brand with an empty m[] shows no model step and goes straight to the
     brand collection.                                                     */
  var BIKES = {
    'Royal Enfield': { c: 'royal-enfield-motorcycle-accessories-1', m: [
      ['Classic 350',        'royal-enfield-classic-350-accessories-1'],
      ['Hunter 350',         'royal-enfield-hunter-350-accessories-1'],
      ['Meteor 350',         'royal-enfield-meteor-350-accessories-1'],
      ['Bullet 350',          null],
      ['Himalayan 450',      'royal-enfield-himalayan-450-accessories'],
      ['Himalayan 411',      'himalayan-411'],
      ['Scram 411',           null],
      ['Guerrilla 450',      'royal-enfield-guerilla-450-accessories'],
      ['Interceptor 650',    'interceptor-650'],
      ['Continental GT 650', 'continental-gt-650'],
      ['Super Meteor 650',   'super-meteor-652'],
      ['Bear 650',           'royal-enfield-bear-650'],
      ['Shotgun 650',         null],
      ['Classic 650',         null]
    ]},
    'KTM': { c: 'ktm-motorcycle-accessories-1', m: [
      ['Duke 390',      'ktm-duke-390-accessories-1'],
      ['390 Adventure', 'ktm-390-adventure-adventure-x-accessories'],
      ['RC 390',         null],
      ['Duke 250',       null],
      ['Duke 200',       null],
      ['Duke 125',       null],
      ['250 Adventure',  null]
    ]},
    'TVS': { c: 'tvs-motorcycle-accessories-1', m: [
      ['Apache RTX 300',    'tvs-rtx-300-accesories'],
      ['Apache RR 310',      null],
      ['Apache RTR 200 4V',  null],
      ['Apache RTR 160 4V',  null],
      ['Ronin',              null],
      ['Raider 125',         null],
      ['Ntorq 125',          null],
      ['Jupiter',            null]
    ]},
    'Honda': { c: 'honda-motorcycle-accessories-1', m: [
      ['CB350 H’ness', 'hness-350'],
      ['CB350RS',           'honda-cb350rs-accessories-1'],
      ['CB350C',            'honda-cb350c-accessories'],
      ['CB200X',            'cb-200-x'],
      ['CB300R',             null],
      ['CB300F',             null],
      ['Hornet 2.0',         null],
      ['X-Blade',            null],
      ['Unicorn',            null]
    ]},
    'Hero': { c: 'hero-motorcycle-accessories-1', m: [
      ['XPulse 200 4V', 'hero-xpulse-200-210-performance-upgrades'],
      ['XPulse 210',    'hero-xpulse-200-210-performance-upgrades']
    ]},
    'Bajaj': { c: 'bajaj-motorcycle-accessories-1', m: [
      ['Pulsar NS400Z', null],
      ['Pulsar NS200',  null],
      ['Pulsar NS160',  null],
      ['Pulsar RS200',  null],
      ['Pulsar 220F',   null],
      ['Dominar 400',   null],
      ['Dominar 250',   null],
      ['Avenger 220',   null]
    ]},
    'Yamaha': { c: 'yamaha-motorcycle-accessories', m: [
      ['MT-15',    null],
      ['R15 V4',   null],
      ['FZ-S V3',  null],
      ['FZS 25',   null],
      ['R3',       null],
      ['MT-03',    null]
    ]},
    'Suzuki': { c: 'suzuki-motorcycle-accessories-1', m: [
      ['Gixxer 250',     null],
      ['Gixxer SF 250',  null],
      ['Gixxer 150',     null],
      ['Access 125',     null],
      ['Burgman Street', null]
    ]},
    'Triumph': { c: 'triumph-motorcycle-accessories-2', m: [
      ['Scrambler 400 X', 'triumph-scrambler-400-x-accessories-1'],
      ['Speed 400',        null],
      ['Speed T4',         null],
      ['Tiger Sport 660',  null]
    ]},
    'BMW': { c: 'bmw-motorcycle-accessories-1', m: [
      ['G 310 GS', 'bmw-g-310-gs-accessories-1'],
      ['G 310 R',   null],
      ['G 310 RR',  null]
    ]},
    'Kawasaki':        { c: 'kawasaki-motorcycle-accessories-1',        m: [] },
    'Harley-Davidson': { c: 'harley-davidson-motorcycle-accessories-1', m: [] },
    'Husqvarna':       { c: null, m: [
      ['Svartpilen 250', null], ['Vitpilen 250', null],
      ['Svartpilen 401', null], ['Vitpilen 401', null]
    ]},
    'Benelli':      { c: 'benelli-motorcycle-accessories',    m: [] },
    'Jawa & Yezdi': { c: 'jawa-yezdi-motorcycle-accessories', m: [] }
  };

  /* CARS: brand -> collection handle (null = fall back to search) */
  var CARS = {
    'Maruti Suzuki': 'maruti-suzuki-car-performance-parts-accessories',
    'Hyundai':       'hyundai-car-performance-parts-accessories',
    'Toyota':        'toyota-car-performance-parts-accessories',
    'Tata':          'tata-car-performance-parts-accessories',
    'Mahindra':      'mahindra-car-performance-parts-accessories',
    'Honda':         'honda',
    'Volkswagen':    'volkswagen-car-performance-parts-accessories',
    'Skoda':         'skoda-car-performance-parts-accessories',
    'Audi':          'audi-car-performance-parts-accessories',
    'BMW':           'bmw',
    'Mercedes-Benz': 'mercedes-benz-car-performance-parts-accessories',
    'Porsche':       'porsche-car-performance-parts-accessories',
    'Volvo':         'volvo-car-performance-parts-accessories',
    'Ford':          'ford',
    'Jeep':          'jeep',
    'Nissan':        'nissan',
    'Renault':       'renault',
    'Kia':           'kia',
    'Mitsubishi':    'mitsubishi',
    'Chevrolet':     'chevrolet',
    'Fiat':          'fiat',
    'Isuzu':         'isuzu',
    'MG':            'mg',
    'Citroen':        null
  };

  var POPULAR = [
    { t: 'TVS RTX 300',   type: 'bike', brand: 'TVS',           model: 'Apache RTX 300' },
    { t: 'Classic 350',   type: 'bike', brand: 'Royal Enfield', model: 'Classic 350' },
    { t: 'Himalayan 450', type: 'bike', brand: 'Royal Enfield', model: 'Himalayan 450' },
    { t: 'Honda CB350RS', type: 'bike', brand: 'Honda',         model: 'CB350RS' },
    { t: 'Duke 390',      type: 'bike', brand: 'KTM',           model: 'Duke 390' },
    { t: 'Hunter 350',    type: 'bike', brand: 'Royal Enfield', model: 'Hunter 350' }
  ];

  var PART_SEARCH_URL = '/pages/part-search';

  /* -- ROUTING --------------------------------------------------------- */
  function searchUrl(term) { return '/search?q=' + encodeURIComponent(term) + '&type=product'; }
  function collUrl(handle) { return '/collections/' + handle; }

  function modelHandle(brand, model) {
    var b = BIKES[brand]; if (!b) return null;
    for (var i = 0; i < b.m.length; i++) if (b.m[i][0] === model) return b.m[i][1];
    return null;
  }

  /* The single routing decision, shared with the Part Search page. */
  function urlFor(type, brand, model) {
    if (type === 'car') {
      if (!brand) return collUrl('shop-by-car-premium-automotive-accessories');
      return CARS[brand] ? collUrl(CARS[brand]) : searchUrl(brand);
    }
    if (!brand) return collUrl('motorcycle');
    if (model) {
      var mh = modelHandle(brand, model);
      if (mh) return collUrl(mh);
      return searchUrl(model);           // no collection yet -> keyword search
    }
    var bh = BIKES[brand] && BIKES[brand].c;
    return bh ? collUrl(bh) : searchUrl(brand);
  }

  function go(url) { if (url) window.location.href = url; }

  /* Flat index for free-text search (bike models + all brands) */
  var SEARCH_INDEX = [];
  Object.keys(BIKES).forEach(function (b) {
    SEARCH_INDEX.push({ brand: b, model: 'All models', term: b, type: 'bike', url: urlFor('bike', b, '') });
    BIKES[b].m.forEach(function (pair) {
      SEARCH_INDEX.push({ brand: b, model: pair[0], term: pair[0], type: 'bike', url: urlFor('bike', b, pair[0]) });
    });
  });
  Object.keys(CARS).forEach(function (b) {
    SEARCH_INDEX.push({ brand: b, model: 'Car parts', term: b, type: 'car', url: urlFor('car', b, '') });
  });

  /* Public surface - the Part Search page inline finder reads this */
  window.RT_FITMENT = {
    version: '2.0',
    BIKES: BIKES,
    CARS: CARS,
    POPULAR: POPULAR,
    INDEX: SEARCH_INDEX,
    urlFor: urlFor,
    searchUrl: searchUrl
  };
  try { document.dispatchEvent(new CustomEvent('rt:fitment-ready')); } catch (e) {}

  /* -- STATE ----------------------------------------------------------- */
  var pop, backdrop, trigger = null;
  var state = { type: 'bike', brand: '', model: '' };

  /* -- BUILD POPOVER --------------------------------------------------- */
  var ICON_BIKE = '<svg viewBox="0 0 24 24" fill="none"><circle cx="5.5" cy="17.5" r="2.5" stroke="currentColor" stroke-width="1.6"/><circle cx="18.5" cy="17.5" r="2.5" stroke="currentColor" stroke-width="1.6"/><path d="M5.5 17.5L8 11l5 .5 3-4 2.5 2.5M14 11.5l1 6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var ICON_CAR  = '<svg viewBox="0 0 24 24" fill="none"><rect x="2" y="9" width="20" height="10" rx="2" stroke="currentColor" stroke-width="1.6"/><path d="M5 9l2-4h10l2 4" stroke="currentColor" stroke-width="1.6"/><circle cx="7.5" cy="19" r="1.5" fill="currentColor"/><circle cx="16.5" cy="19" r="1.5" fill="currentColor"/></svg>';
  var ICON_SEARCH = '<svg viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="1.8"/><path d="M20 20l-3-3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';

  function esc(s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;'); }

  function build() {
    if (document.getElementById('rt-finder-pop')) return;

    backdrop = document.createElement('div');
    backdrop.id = 'rt-finder-backdrop';
    document.body.appendChild(backdrop);

    pop = document.createElement('div');
    pop.id = 'rt-finder-pop';
    pop.setAttribute('role', 'dialog');
    pop.setAttribute('aria-label', 'Find parts for your ride');

    var chips = POPULAR.map(function (c) {
      return '<a class="rt-fp__chip" href="' + esc(urlFor(c.type, c.brand, c.model)) + '">' + esc(c.t) + '</a>';
    }).join('');

    pop.innerHTML =
      '<div class="rt-fp__card">' +
        '<div class="rt-fp__head">' +
          '<span class="rt-fp__eyebrow">Fitment Search</span>' +
          '<span class="rt-fp__title">Find parts for <b>your ride</b></span>' +
        '</div>' +
        '<div class="rt-fp__seg" role="tablist">' +
          '<button type="button" class="rt-fp__seg-btn is-active" data-type="bike">' + ICON_BIKE + 'Bike</button>' +
          '<button type="button" class="rt-fp__seg-btn" data-type="car">' + ICON_CAR + 'Car</button>' +
        '</div>' +
        '<div class="rt-fp__row">' +
          '<div class="rt-fp__dd" data-dd="brand">' +
            '<button type="button" class="rt-fp__dd-trigger"><span class="rt-fp__dd-lbl">Select brand</span><i class="rt-fp__dd-arr"></i></button>' +
            '<div class="rt-fp__dd-list" role="listbox"></div>' +
          '</div>' +
          '<div class="rt-fp__dd is-disabled" data-dd="model">' +
            '<button type="button" class="rt-fp__dd-trigger"><span class="rt-fp__dd-lbl">Model (optional)</span><i class="rt-fp__dd-arr"></i></button>' +
            '<div class="rt-fp__dd-list" role="listbox"></div>' +
          '</div>' +
        '</div>' +
        '<button type="button" class="rt-fp__go" disabled>' + ICON_SEARCH + 'Find parts</button>' +
        '<div class="rt-fp__or"><span>or</span></div>' +
        '<div class="rt-fp__search">' +
          ICON_SEARCH.replace('<svg', '<svg class="rt-fp__search-ico"') +
          '<input type="search" class="rt-fp__search-inp" placeholder="Type your bike or car…" autocomplete="off">' +
          '<div class="rt-fp__sugg" role="listbox"></div>' +
        '</div>' +
        '<div class="rt-fp__pop"><span class="rt-fp__pop-lbl">Popular</span>' + chips + '</div>' +
        '<a class="rt-fp__all" href="' + PART_SEARCH_URL + '">Browse every brand →</a>' +
        '<a class="rt-fp__all" href="' + PART_SEARCH_URL + '#help" style="margin-top:6px;padding-top:0;border-top:0;">Not sure what fits? Get fitment help</a>' +
      '</div>';

    document.body.appendChild(pop);
    wire();
    renderBrands();
  }

  /* -- DROPDOWN RENDERING ---------------------------------------------- */
  function ddEl(which) { return pop.querySelector('.rt-fp__dd[data-dd="' + which + '"]'); }
  function setLabel(which, text, filled) {
    var dd = ddEl(which);
    dd.querySelector('.rt-fp__dd-lbl').textContent = text;
    dd.classList.toggle('has-val', !!filled);
  }
  function closeAllDD() { pop.querySelectorAll('.rt-fp__dd.is-open').forEach(function (d) { d.classList.remove('is-open'); }); }

  function renderBrands() {
    var list = ddEl('brand').querySelector('.rt-fp__dd-list');
    var brands = state.type === 'car' ? Object.keys(CARS) : Object.keys(BIKES);
    list.innerHTML = brands.map(function (b) {
      return '<div class="rt-fp__opt' + (state.brand === b ? ' is-sel' : '') + '" data-val="' + esc(b) + '">' + esc(b) + '</div>';
    }).join('');
  }
  function renderModels(brand) {
    var dd = ddEl('model');
    var list = dd.querySelector('.rt-fp__dd-list');
    var models = (BIKES[brand] && BIKES[brand].m) || [];
    if (!models.length) {                      // brand has no model split yet
      list.innerHTML = '';
      dd.classList.add('is-disabled');
      setLabel('model', 'All models', false);
      return;
    }
    list.innerHTML = '<div class="rt-fp__opt" data-val="">All models</div>' + models.map(function (pair) {
      return '<div class="rt-fp__opt" data-val="' + esc(pair[0]) + '">' + esc(pair[0]) + '</div>';
    }).join('');
    dd.classList.remove('is-disabled');
  }

  /* brand alone is enough now - model only narrows it */
  function refreshGo() { pop.querySelector('.rt-fp__go').disabled = !state.brand; }

  function submitVehicle() {
    if (!state.brand) return;
    go(urlFor(state.type, state.brand, state.model));
  }

  /* -- WIRING ---------------------------------------------------------- */
  function wire() {
    /* segmented toggle */
    pop.querySelectorAll('.rt-fp__seg-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.dataset.type === state.type) return;
        pop.querySelectorAll('.rt-fp__seg-btn').forEach(function (b) { b.classList.remove('is-active'); });
        btn.classList.add('is-active');
        state.type = btn.dataset.type;
        state.brand = ''; state.model = '';
        closeAllDD();
        setLabel('brand', 'Select brand', false);
        setLabel('model', state.type === 'car' ? 'Not required' : 'Model (optional)', false);
        ddEl('model').classList.add('is-disabled');
        ddEl('model').style.display = state.type === 'car' ? 'none' : '';
        renderBrands();
        refreshGo();
      });
    });

    /* dropdown open/close */
    pop.querySelectorAll('.rt-fp__dd').forEach(function (dd) {
      dd.querySelector('.rt-fp__dd-trigger').addEventListener('click', function (e) {
        e.stopPropagation();
        if (dd.classList.contains('is-disabled')) return;
        var wasOpen = dd.classList.contains('is-open');
        closeAllDD();
        if (!wasOpen) dd.classList.add('is-open');
      });
    });

    /* brand pick */
    ddEl('brand').querySelector('.rt-fp__dd-list').addEventListener('click', function (e) {
      var opt = e.target.closest('.rt-fp__opt'); if (!opt) return;
      state.brand = opt.dataset.val; state.model = '';
      setLabel('brand', state.brand, true);
      ddEl('brand').classList.remove('is-open');
      if (state.type === 'bike') {
        setLabel('model', 'Model (optional)', false);
        renderModels(state.brand);
      }
      refreshGo();
    });

    /* model pick */
    ddEl('model').querySelector('.rt-fp__dd-list').addEventListener('click', function (e) {
      var opt = e.target.closest('.rt-fp__opt'); if (!opt) return;
      state.model = opt.dataset.val;
      setLabel('model', state.model || 'All models', !!state.model);
      ddEl('model').classList.remove('is-open');
      refreshGo();
    });

    /* go */
    pop.querySelector('.rt-fp__go').addEventListener('click', submitVehicle);

    /* free-text search */
    var inp = pop.querySelector('.rt-fp__search-inp');
    var sugg = pop.querySelector('.rt-fp__sugg');
    var hl = -1;

    function items() { return sugg.querySelectorAll('.rt-fp__sugg-item'); }
    inp.addEventListener('input', function () {
      var q = this.value.trim().toLowerCase(); hl = -1;
      if (q.length < 2) { sugg.classList.remove('is-open'); sugg.innerHTML = ''; return; }
      var res = SEARCH_INDEX.filter(function (x) {
        return x.model.toLowerCase().indexOf(q) !== -1 || x.brand.toLowerCase().indexOf(q) !== -1;
      }).slice(0, 8);
      if (!res.length) { sugg.classList.remove('is-open'); sugg.innerHTML = ''; return; }
      sugg.innerHTML = res.map(function (x) {
        return '<div class="rt-fp__sugg-item" data-url="' + esc(x.url) + '"><span class="rt-fp__sugg-brand">' + esc(x.brand) + '</span>' + esc(x.model) + '</div>';
      }).join('');
      sugg.classList.add('is-open');
    });
    sugg.addEventListener('mousedown', function (e) {
      var it = e.target.closest('.rt-fp__sugg-item'); if (!it) return;
      e.preventDefault(); go(it.dataset.url);
    });
    inp.addEventListener('keydown', function (e) {
      var its = items();
      if (e.key === 'ArrowDown') { e.preventDefault(); hl = Math.min(hl + 1, its.length - 1); its.forEach(function (x, i) { x.classList.toggle('is-hl', i === hl); }); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); hl = Math.max(hl - 1, -1); its.forEach(function (x, i) { x.classList.toggle('is-hl', i === hl); }); }
      else if (e.key === 'Enter') {
        e.preventDefault();
        if (hl >= 0 && its[hl]) go(its[hl].dataset.url);
        else { var v = inp.value.trim(); if (v) go(searchUrl(v)); }
      } else if (e.key === 'Escape') { sugg.classList.remove('is-open'); }
    });

    /* close dropdowns / suggestions when clicking elsewhere inside the popover */
    pop.addEventListener('click', function (e) {
      if (!e.target.closest('.rt-fp__dd')) closeAllDD();
      if (!e.target.closest('.rt-fp__search')) sugg.classList.remove('is-open');
    });

    /* chips & catalogue link close the popover naturally via navigation */
  }

  /* -- OPEN / CLOSE / POSITION ----------------------------------------- */
  function position() {
    if (!trigger || !pop) return;
    var r = trigger.getBoundingClientRect();
    var w = pop.offsetWidth || 360;
    var left = Math.min(r.left, window.innerWidth - w - 12);
    left = Math.max(12, left);
    pop.style.top = Math.round(r.bottom + 8) + 'px';
    pop.style.left = Math.round(left) + 'px';
  }

  function open(fromTrigger) {
    build();
    trigger = fromTrigger;
    position();
    pop.classList.add('is-open');
    backdrop.classList.add('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    document.addEventListener('keydown', onEsc);
    window.addEventListener('scroll', position, { passive: true });
    window.addEventListener('resize', onResize);
  }

  function close() {
    if (!pop) return;
    pop.classList.remove('is-open');
    backdrop.classList.remove('is-open');
    closeAllDD();
    var s = pop.querySelector('.rt-fp__sugg'); if (s) s.classList.remove('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', onEsc);
    window.removeEventListener('scroll', position);
    window.removeEventListener('resize', onResize);
  }

  function isOpen() { return pop && pop.classList.contains('is-open'); }
  function onEsc(e) { if (e.key === 'Escape') close(); }
  function onResize() {
    if (window.innerWidth <= MOBILE_MAX) { close(); return; } // dropped to mobile -> let native drawer own it
    position();
  }

  backdropClose();
  function backdropClose() {
    document.addEventListener('click', function (e) {
      if (!isOpen()) return;
      if (e.target.closest('#rt-finder-pop')) return;
      if (e.target.closest('a[aria-controls="nav"]')) return; // handled by intercept below
      close();
    });
  }

  /* -- DESKTOP INTERCEPT (capture phase) ------------------------------- */
  document.addEventListener('click', function (e) {
    if (window.innerWidth <= MOBILE_MAX) return;        // mobile: native drawer untouched
    var a = e.target.closest('a[aria-controls="nav"]'); // the Find Parts button(s)
    if (!a) return;
    e.preventDefault();
    e.stopImmediatePropagation();                       // stop theme's nav-burger (bubble phase)
    if (isOpen()) close(); else open(a);
  }, true);

  /* keyboard activation (space) of the Find Parts button on desktop */
  document.addEventListener('keyup', function (e) {
    if (window.innerWidth <= MOBILE_MAX) return;
    if (e.key !== ' ' && e.key !== 'Enter') return;
    var a = e.target.closest && e.target.closest('a[aria-controls="nav"]');
    if (!a) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (isOpen()) close(); else open(a);
  }, true);
})();